import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import api from '../../services/api';
import CreditLedgerSection from '../../components/admin/CreditLedgerSection';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  Calendar,
  GraduationCap,
  Clock,
  MapPin,
  Pencil,
  CheckCircle,
  XCircle,
  MessageSquare,
  Activity,
} from 'lucide-react';

const CoachDetail = () => {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [profile, setProfile] = useState(null);
  const [classes, setClasses] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [locations, setLocations] = useState([]);
  
  // Filters
  const [billingMonth, setBillingMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coachForm, setCoachForm] = useState({});

  useEffect(() => {
    fetchCoachDetails();
    fetchLocations();
  }, [coachId]);

  const fetchLocations = async () => {
    try {
      const res = await api.get('/locations/');
      setLocations(res.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchCoachDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch coach details
      const coachRes = await api.get(`/coaches/${coachId}`);
      setCoach(coachRes.data);
      setProfile(coachRes.data.profile || {});
      setCoachForm({
        name: coachRes.data.name || '',
        email: coachRes.data.email || '',
        phone: coachRes.data.phone || '',
        specialization: coachRes.data.profile?.specialization || '',
        hourly_rate: coachRes.data.profile?.hourly_rate || 25,
        bio: coachRes.data.profile?.bio || '',
        location_ids: coachRes.data.profile?.location_ids || []
      });
      
      // Fetch classes assigned to this coach
      try {
        const classesRes = await api.get(`/classes?coach_id=${coachId}`);
        setClasses(classesRes.data || []);
      } catch {
        setClasses([]);
      }
      
      // Fetch billing history
      try {
        const [year, month] = billingMonth.split('-');
        const billingRes = await api.get(`/admin/billing-summary?month=${parseInt(month)}&year=${parseInt(year)}`);
        const coachBilling = billingRes.data.find(b => b.coach_id === coachId);
        if (coachBilling) {
          setBillingHistory(coachBilling.entries || []);
        }
      } catch {
        setBillingHistory([]);
      }
      
      // Fetch coach notes
      try {
        const notesRes = await api.get(`/coach-notes?coach_id=${coachId}`);
        setNotes(notesRes.data || []);
      } catch {
        setNotes([]);
      }
      
    } catch (error) {
      toast.error('Failed to load coach details');
      navigate('/admin/coaches');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingForMonth = async (month) => {
    try {
      const [year, monthNum] = month.split('-');
      const billingRes = await api.get(`/admin/billing-summary?month=${parseInt(monthNum)}&year=${parseInt(year)}`);
      const coachBilling = billingRes.data.find(b => b.coach_id === coachId);
      if (coachBilling) {
        setBillingHistory(coachBilling.entries || []);
      } else {
        setBillingHistory([]);
      }
    } catch {
      setBillingHistory([]);
    }
  };

  const handleSaveCoach = async () => {
    try {
      setSaving(true);
      await api.put(`/coaches/${coachId}`, coachForm);
      toast.success('Coach updated successfully');
      setEditDialog(false);
      fetchCoachDetails();
    } catch (error) {
      toast.error('Failed to update coach');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} capitalize`}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Coach Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!coach) {
    return (
      <DashboardLayout title="Coach Details">
        <div className="text-center py-12">
          <p className="text-slate-500">Coach not found</p>
          <Button onClick={() => navigate('/admin/coaches')} className="mt-4">
            Back to Coaches
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const totalApprovedHours = billingHistory.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.hours_worked, 0);
  const totalPendingHours = billingHistory.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.hours_worked, 0);
  const totalRejectedHours = billingHistory.filter(e => e.status === 'rejected').reduce((sum, e) => sum + e.hours_worked, 0);

  return (
    <DashboardLayout title="Coach Details">
      <div className="space-y-6" data-testid="coach-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/admin/coaches')}
              data-testid="back-to-coaches"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold text-xl">
                  {coach.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-900">{coach.name}</h1>
                  {coach.numeric_id && (
                    <Badge variant="outline" className="text-xs font-mono bg-slate-100">
                      Coach ID: {coach.numeric_id}
                    </Badge>
                  )}
                </div>
                <p className="text-slate-500">
                  {profile?.specialization || 'Coach'} • {cs()}{profile?.hourly_rate || 25}/hr
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={profile?.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
              {profile?.is_active === false ? 'inactive' : 'active'}
            </Badge>
            <Button onClick={() => setEditDialog(true)} data-testid="edit-coach-btn">
              <Pencil className="h-4 w-4 mr-2" />
              Edit Coach
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full max-w-xl overflow-x-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes ({notes.length})</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Name</p>
                      <p className="font-medium">{coach.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <a href={`mailto:${coach.email}`} className="font-medium text-blue-600 hover:underline">
                        {coach.email}
                      </a>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      {coach.phone ? (
                        <a href={`tel:${coach.phone}`} className="font-medium text-blue-600 hover:underline">
                          {coach.phone}
                        </a>
                      ) : (
                        <span className="text-slate-400">Not provided</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Status</p>
                      <Badge className={profile?.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                        {profile?.is_active === false ? 'inactive' : 'active'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Profile Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5" />
                    Coach Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Specialization</p>
                      <p className="font-medium">{profile?.specialization || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Hourly Rate</p>
                      <p className="font-medium text-green-600">{cs()}{profile?.hourly_rate || 25}/hr</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Classes Assigned</p>
                      <p className="font-medium">{classes.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Member Since</p>
                      <p className="font-medium">{new Date(coach.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  {/* Work Locations */}
                  <div className="pt-4 border-t">
                    <p className="text-sm text-slate-500 mb-2">Work Locations</p>
                    {profile?.location_ids && profile.location_ids.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {profile.location_ids.map((locId) => {
                          const loc = locations.find(l => l.id === locId);
                          return loc ? (
                            <Badge key={locId} variant="outline" className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {loc.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm">No locations assigned</p>
                    )}
                  </div>
                  
                  {profile?.bio && (
                    <div className="pt-4 border-t">
                      <p className="text-sm text-slate-500 mb-2">Bio</p>
                      <p className="text-slate-700">{profile.bio}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    This Month's Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{classes.length}</p>
                      <p className="text-sm text-blue-600">Classes</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{totalApprovedHours.toFixed(1)}</p>
                      <p className="text-sm text-green-600">Approved Hours</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{totalPendingHours.toFixed(1)}</p>
                      <p className="text-sm text-amber-600">Pending Hours</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-600">
                        {cs()}{(totalApprovedHours * (profile?.hourly_rate || 25)).toFixed(2)}
                      </p>
                      <p className="text-sm text-slate-600">Earnings</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Assigned Classes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {classes.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Class Name</TableHead>
                        <TableHead>Program</TableHead>
                        <TableHead>Schedule</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Enrolled</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {classes.map((cls) => (
                        <TableRow key={cls.id}>
                          <TableCell className="font-medium">{cls.name}</TableCell>
                          <TableCell>{cls.program_name || '-'}</TableCell>
                          <TableCell>
                            <div>
                              <p>{cls.day_of_week} {cls.start_time} - {cls.end_time}</p>
                            </div>
                          </TableCell>
                          <TableCell>{cls.location_name || '-'}</TableCell>
                          <TableCell>
                            <Badge className="bg-blue-100 text-blue-700">
                              {cls.current_enrollment || 0} / {cls.capacity || 20}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No classes assigned yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="mt-6">
            <div className="space-y-6">
              {/* iter307cz — Credit ledger section (role-agnostic) */}
              <CreditLedgerSection userId={coachId} />
              {/* Month Selector */}
              <Card>
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <Label>Select Month:</Label>
                    <Input
                      type="month"
                      value={billingMonth}
                      onChange={(e) => {
                        setBillingMonth(e.target.value);
                        fetchBillingForMonth(e.target.value);
                      }}
                      className="w-48"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-3xl font-bold text-slate-900">
                      {billingHistory.reduce((sum, e) => sum + e.hours_worked, 0).toFixed(1)}
                    </p>
                    <p className="text-sm text-slate-500">Total Hours</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-3xl font-bold text-green-600">{totalApprovedHours.toFixed(1)}</p>
                    <p className="text-sm text-green-600">Approved</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-3xl font-bold text-amber-600">{totalPendingHours.toFixed(1)}</p>
                    <p className="text-sm text-amber-600">Pending</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-3xl font-bold text-red-600">{totalRejectedHours.toFixed(1)}</p>
                    <p className="text-sm text-red-600">Rejected</p>
                  </CardContent>
                </Card>
              </div>

              {/* Time Entries Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Time Entries
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {billingHistory.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Class</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {billingHistory.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                            <TableCell>{entry.class_name || '-'}</TableCell>
                            <TableCell className="font-medium">{entry.hours_worked} hrs</TableCell>
                            <TableCell>{getStatusBadge(entry.status)}</TableCell>
                            <TableCell className="max-w-xs truncate text-slate-500">
                              {entry.notes || entry.admin_note || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Clock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No time entries for this month</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Coach Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notes.length > 0 ? (
                  <div className="space-y-4">
                    {notes.map((note) => (
                      <div key={note.id} className={`p-4 rounded-lg border ${note.is_read ? 'bg-slate-50' : 'bg-blue-50 border-blue-200'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={
                                note.note_type === 'class' ? 'bg-blue-100 text-blue-700' :
                                note.note_type === 'trial_player' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {note.note_type || 'General'}
                              </Badge>
                              {note.is_urgent && (
                                <Badge className="bg-red-100 text-red-700">Urgent</Badge>
                              )}
                              {!note.is_read && (
                                <Badge className="bg-blue-100 text-blue-700">Unread</Badge>
                              )}
                            </div>
                            <p className="text-slate-700">{note.content}</p>
                            {note.class_name && (
                              <p className="text-sm text-slate-500 mt-2">Class: {note.class_name}</p>
                            )}
                            {note.player_name && (
                              <p className="text-sm text-slate-500">Player: {note.player_name}</p>
                            )}
                          </div>
                          <span className="text-xs text-slate-400">
                            {new Date(note.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No notes from this coach yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Coach Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Coach</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={coachForm.name}
                  onChange={(e) => setCoachForm({ ...coachForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={coachForm.email}
                  onChange={(e) => setCoachForm({ ...coachForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={coachForm.phone}
                  onChange={(e) => setCoachForm({ ...coachForm, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Specialization</Label>
                <Input
                  value={coachForm.specialization}
                  onChange={(e) => setCoachForm({ ...coachForm, specialization: e.target.value })}
                  placeholder="e.g., Batting, Bowling, Fielding"
                />
              </div>
              <div className="space-y-2">
                <Label>Hourly Rate ($)</Label>
                <Input
                  type="number"
                  value={coachForm.hourly_rate}
                  onChange={(e) => setCoachForm({ ...coachForm, hourly_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea
                  value={coachForm.bio}
                  onChange={(e) => setCoachForm({ ...coachForm, bio: e.target.value })}
                  placeholder="Brief bio about the coach..."
                />
              </div>
              <div className="space-y-2">
                <Label>Work Locations</Label>
                <p className="text-sm text-slate-500 mb-2">Select the locations where this coach can take classes</p>
                <div className="space-y-2 border rounded-md p-3">
                  {locations.length > 0 ? (
                    locations.map((loc) => (
                      <div key={loc.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`loc-${loc.id}`}
                          checked={coachForm.location_ids?.includes(loc.id)}
                          onCheckedChange={(checked) => {
                            const currentIds = coachForm.location_ids || [];
                            if (checked) {
                              setCoachForm({ ...coachForm, location_ids: [...currentIds, loc.id] });
                            } else {
                              setCoachForm({ ...coachForm, location_ids: currentIds.filter(id => id !== loc.id) });
                            }
                          }}
                        />
                        <label
                          htmlFor={`loc-${loc.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                        >
                          <MapPin className="h-3 w-3 text-slate-400" />
                          {loc.name} {loc.is_primary && <Badge variant="outline" className="text-xs">Primary</Badge>}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-400">No locations configured</p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveCoach} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CoachDetail;
